import { CURVE_COLORS, TECH_REP_COLORS } from '@resistapp/client/components/plots/chip-view-constant';
import { KeysPressOptions } from '@resistapp/client/components/plots/legends/legend';
import { Curve } from '@resistapp/client/components/plots/simple-line-chart';
import { LocalStorageId } from '@resistapp/client/components/shared/local-storage';
import { useSearchParamsContext } from '@resistapp/client/contexts/search-params-context';
import { useChipDebug } from '@resistapp/client/hooks/api';
import {
  ChipViewHighlightMode,
  chipViewHighlightSelectedValue,
  getChipViewHighlightMode,
  StoredChipViewHighlightValue,
} from '@resistapp/client/utils/chip-view-highlight-mode';
import { isQaResultLabelDisqualifying } from '@resistapp/common/qa-label-utils';
import { compareCtsIdentifiers } from '@resistapp/common/cts-order';
import {
  availableFlags,
  Chip,
  ChipWarning,
  LabWorkbookLink,
  QaLabel,
  QaLabelScope,
  QaLabelType,
  QaResolvedLabelSummary,
  SmartChipFlag,
  WellDebugData,
} from '@resistapp/common/types';
import { SampleSubType, SampleType } from '@resistapp/common/sampling-point-types';
import { uniq } from 'lodash';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import useLocalStorageState from 'use-local-storage-state';
import { useNavigateWithQuery } from '@resistapp/client/hooks/use-navigate-with-query';
import {
  buildAddedSelectionParamList,
  buildCtrlToggledParamList,
  buildSelectionRangeParamList,
} from './selection-param-utils';
import { buildCanonicalChipPath } from './chip-path-utils';
import {
  getChipWarningAssayFocusSelection,
  getChipWarningSampleAssayFocusSelection,
  getChipWarningSampleFocusSelection,
} from './chip-warning-focus-utils';
import { getNoAmplificationHighlights } from './chip-warning-utils';

interface HeatmapDatum {
  row: number;
  column: number;
  assay: string;
  sample: string;
  sampleName: string | null;
  sampleType: SampleType | null;
  sampleSubtype: SampleSubType | null;
  ct: number | null;
  tm: number | null;
  efficiency: number | null;
  flags: string | null;
  selectedAssays: string[] | null;
  rejectRule: number | 'M' | null;
  qaResultLabelType: QaLabelType | null;
  qaResultLabelScope: QaLabelScope | null;
  qaLabels: QaResolvedLabelSummary[];
}

interface ChipContextData {
  loading: boolean;
  chipInfo: Chip | undefined;
  workbookLink: LabWorkbookLink | null;
  projects: Array<{ id: number; name: string }>;
  qaLabels: QaLabel[];
  warnings: ChipWarning[];
  selectedSamples: string[] | null;
  selectedGroups: string[] | null;
  sampleKeys: string[];
  uniqueGroups: string[];
  assayColorByAssay: Record<string, string>;
  sampleColorByKey: Record<string, string>;
  colorMode: 'ASSAY' | 'SAMPLE';
  heatmapData: HeatmapDatum[];
  ampCurves: Curve[];
  derCurves: Curve[];
  flaggedAssays: string[];
  flaggedSamples: string[];
  sampleLegendItems: Array<{ label: string; displayLabel: string; color: string }>;
  selectedMetric: 'ct' | 'efficiency';
  setSelectedMetricStable: React.Dispatch<React.SetStateAction<'ct' | 'efficiency'>>;
  highlightFlag: ChipViewHighlightMode;
  setHighlightFlagStable: React.Dispatch<React.SetStateAction<ChipViewHighlightMode>>;
  flagOptions: Array<{ label: string; value: ChipViewHighlightMode }>;
  showRejected: boolean;
  setShowRejectedStable: React.Dispatch<React.SetStateAction<boolean>>;
  toggleSampleStable: (label: string, keys: KeysPressOptions) => void;
  toggleGroupStable: (assay: string, keys: KeysPressOptions) => void;
  handleCellClickStable: (sample: string, assay: string, keys: KeysPressOptions, isSelected: boolean) => void;
  focusSampleStable: (technicalSample: string) => void;
  focusGroupStable: (assay: string) => void;
  focusBiologicalSampleStable: (baseSampleId: string) => void;
  focusBiologicalSampleAssayStable: (baseSampleId: string, assay: string) => void;
  focusFlagStable: (flag: SmartChipFlag) => void;
  debugData: WellDebugData[];
  rejectionById: Record<number, number | 'M' | null>;
}

const ChipContext = createContext<ChipContextData | undefined>(undefined);

export function ChipContextProvider({ children }: { children: React.ReactNode }) {
  const { chipId } = useParams();
  const location = useLocation();
  const navigate = useNavigateWithQuery();
  const { searchParams, setSearchParamsStable } = useSearchParamsContext();
  const { data: debugResp, isLoading } = useChipDebug(chipId || '');

  const { debugData, chipInfo, projects, qaLabels, workbookLink, warnings } = useMemo(() => {
    return {
      debugData: debugResp?.debugData ?? [],
      chipInfo: debugResp?.chip,
      projects: debugResp?.projects ?? [],
      qaLabels: debugResp?.qaLabels ?? [],
      workbookLink: debugResp?.workbook ?? null,
      warnings: debugResp?.warnings ?? [],
    };
  }, [debugResp]);

  useEffect(() => {
    if (!chipInfo || chipId === String(chipInfo.id)) {
      return;
    }

    navigate(buildCanonicalChipPath(chipInfo.id, location.search, location.hash), false, true);
  }, [chipId, chipInfo, location.hash, location.search, navigate]);

  const selectedSamplesParam = searchParams.get('samples');
  const selectedGroupsParam = searchParams.get('ggroups');
  const selectedSamples = useMemo(
    () => (selectedSamplesParam ? selectedSamplesParam.split(',') : null),
    [selectedSamplesParam],
  );
  const selectedGroups = useMemo(
    () => (selectedGroupsParam ? selectedGroupsParam.split(',') : null),
    [selectedGroupsParam],
  );

  // Sort each legend independently: a partial assay panel may omit samples from its first assay.
  const uniqueGroups = useMemo(() => uniq(debugData.map(d => d.cts.assay)).sort(compareCtsIdentifiers), [debugData]);
  const sampleKeys = useMemo(() => uniq(debugData.map(d => d.cts.sample)).sort(compareCtsIdentifiers), [debugData]);

  const defaultColor = CURVE_COLORS[0];
  const dynamicColors = CURVE_COLORS.slice(1);

  const getSampleId = useCallback((sampleKey: string) => sampleKey.replace(/-rep\d+$/, ''), []);

  const colorAssignments = useMemo(() => {
    const sampleColor: Record<string, string> = {};
    const assayColor: Record<string, string> = {};
    const selectedGroupsCount = selectedGroups ? selectedGroups.length : 0;
    const oneAssaySelected = selectedGroupsCount === 1;
    const assaysToColor = oneAssaySelected ? selectedGroups ?? [] : selectedGroups ?? uniqueGroups;

    const maxColors = dynamicColors.length;

    // -------------------- ASSAY COLOURING --------------------
    if (!oneAssaySelected) {
      // If we have more assays to colour than colours available, fall back to default for all.
      if (assaysToColor.length > maxColors) {
        assaysToColor.forEach(assay => {
          assayColor[assay] = defaultColor;
        });
      } else {
        assaysToColor.forEach((assay, idx) => {
          assayColor[assay] = dynamicColors[idx] ?? defaultColor;
        });
      }

      // In assay mode samples always get default colour.
      sampleKeys.forEach(k => {
        sampleColor[k] = defaultColor;
      });
    } else {
      // Ensure the (single) selected assay uses default colour
      if (selectedGroups) {
        const selectedAssay = selectedGroups[0];
        assayColor[selectedAssay] = defaultColor;
      }

      // Determine which sample keys we should consider for colouring.
      const selectionKeys = selectedSamples && selectedSamples.length > 0 ? selectedSamples : sampleKeys;
      const sampleIds = uniq(selectionKeys.map(getSampleId));

      // ---------------- Replicates within a single biological sample ----------------
      if (sampleIds.length === 1) {
        const repsKeys = sampleKeys.filter(k => getSampleId(k) === sampleIds[0]);
        repsKeys.forEach(k => {
          const match = k.match(/-rep(\d+)$/);
          const rep = match ? Number.parseInt(match[1], 10) : NaN;
          const repIdx = Number.isFinite(rep) ? rep - 1 : -1;
          const techRepColor = repIdx >= 0 && repIdx < TECH_REP_COLORS.length ? TECH_REP_COLORS[repIdx] : undefined;
          sampleColor[k] = techRepColor ?? (repIdx >= 0 && repIdx < maxColors ? dynamicColors[repIdx] : defaultColor);
        });
        sampleKeys
          .filter(k => !repsKeys.includes(k))
          .forEach(k => {
            sampleColor[k] = defaultColor;
          });
      }
      // ---------------- Multiple biological samples ----------------
      else if (sampleIds.length <= maxColors) {
        // We have enough colours -> colour each bio-sample individually.
        sampleIds.forEach((id, idx) => {
          const color = dynamicColors[idx] ?? defaultColor;
          sampleKeys
            .filter(k => getSampleId(k) === id)
            .forEach(k => {
              sampleColor[k] = color;
            });
        });
        // Samples not in the current selection (if a subset is selected) stay default.
        sampleKeys
          .filter(k => !selectionKeys.includes(k))
          .forEach(k => {
            sampleColor[k] = defaultColor;
          });
      } else {
        // Too many samples – fall back to default for all.
        sampleKeys.forEach(k => {
          sampleColor[k] = defaultColor;
        });
      }
    }

    sampleKeys.forEach(k => {
      if (!sampleColor[k]) sampleColor[k] = defaultColor;
    });
    uniqueGroups.forEach(assay => {
      if (!assayColor[assay]) assayColor[assay] = defaultColor;
    });

    const mode = !oneAssaySelected ? 'ASSAY' : 'SAMPLE';
    return { sampleColor, assayColor, mode } as const;
  }, [selectedGroups, uniqueGroups, sampleKeys, dynamicColors, defaultColor, selectedSamples, getSampleId]);

  type ParamName = 'samples' | 'ggroups';
  const setParamListStable = useCallback(
    (param: ParamName, list: string[] | null) => {
      setSearchParamsStable(
        prev => {
          const next = new URLSearchParams(prev);
          if (!list || list.length === 0) {
            next.delete(param);
          } else {
            next.set(param, list.join(','));
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParamsStable],
  );

  const lastSampleRef = useRef<string | null>(null);
  const lastAssayRef = useRef<string | null>(null);

  const updateParamSelectionStable = useCallback(
    (param: ParamName, list: string[] | null) => {
      setParamListStable(param, list);
    },
    [setParamListStable],
  );

  const toggleSampleStable = useCallback(
    (label: string, keys: KeysPressOptions) => {
      const all = sampleKeys;
      const currentExplicit = selectedSamples;
      if (keys.shift) {
        const anchor = lastSampleRef.current ?? label;
        const list = buildSelectionRangeParamList(all, anchor, label);
        updateParamSelectionStable('samples', list);
        lastSampleRef.current = label;
        return;
      }
      if (keys.ctrl) {
        const list = buildCtrlToggledParamList(all, currentExplicit, label);
        updateParamSelectionStable('samples', list);
        lastSampleRef.current = label;
        return;
      }
      if (currentExplicit && currentExplicit.length === 1 && currentExplicit[0] === label) {
        updateParamSelectionStable('samples', null);
      } else {
        updateParamSelectionStable('samples', [label]);
      }
      lastSampleRef.current = label;
    },
    [selectedSamples, sampleKeys, updateParamSelectionStable],
  );

  const toggleGroupStable = useCallback(
    (assay: string, keys: KeysPressOptions) => {
      const all = uniqueGroups;
      const currentExplicit = selectedGroups;
      if (keys.shift) {
        const anchor = lastAssayRef.current ?? assay;
        const list = buildSelectionRangeParamList(all, anchor, assay);
        updateParamSelectionStable('ggroups', list);
        lastAssayRef.current = assay;
        return;
      }
      if (keys.ctrl) {
        const list = buildCtrlToggledParamList(all, currentExplicit, assay);
        updateParamSelectionStable('ggroups', list);
        lastAssayRef.current = assay;
        return;
      }
      if (currentExplicit && currentExplicit.length === 1 && currentExplicit[0] === assay) {
        updateParamSelectionStable('ggroups', null);
      } else {
        updateParamSelectionStable('ggroups', [assay]);
      }
      lastAssayRef.current = assay;
    },
    [selectedGroups, uniqueGroups, updateParamSelectionStable],
  );

  const handleCellClickStable = useCallback(
    (sample: string, assay: string, keys: KeysPressOptions, isSelected: boolean) => {
      if (keys.ctrl) {
        if (isSelected) {
          const onlySampleSelectedCtrl = selectedSamples?.length === 1 && selectedSamples[0] === sample;
          const onlyAssaySelectedCtrl = selectedGroups?.length === 1 && selectedGroups[0] === assay;
          if (onlySampleSelectedCtrl && onlyAssaySelectedCtrl) {
            return;
          }
          const isLastSample = selectedSamples ? selectedSamples.length === 1 && selectedSamples[0] === sample : false;
          if (isLastSample) {
            toggleGroupStable(assay, { ctrl: true, shift: false });
          } else {
            toggleSampleStable(sample, { ctrl: true, shift: false });
          }
          return;
        }
        const sampleAlreadySelected = !selectedSamples || selectedSamples.includes(sample);
        const assayAlreadySelected = !selectedGroups || selectedGroups.includes(assay);
        if (sampleAlreadySelected && !assayAlreadySelected) {
          toggleGroupStable(assay, { ctrl: true, shift: false });
        } else if (!sampleAlreadySelected && assayAlreadySelected) {
          toggleSampleStable(sample, { ctrl: true, shift: false });
        } else {
          setSearchParamsStable(
            prev => {
              const next = new URLSearchParams(prev);
              const currentSamplesParam = next.get('samples');
              const currentSamples = currentSamplesParam ? currentSamplesParam.split(',') : null;
              const updatedSamples = buildAddedSelectionParamList(sampleKeys, currentSamples, sample);
              if (!updatedSamples || updatedSamples.length === 0) {
                next.delete('samples');
              } else {
                next.set('samples', updatedSamples.join(','));
              }
              const currentAssaysParam = next.get('ggroups');
              const currentAssays = currentAssaysParam ? currentAssaysParam.split(',') : null;
              const updatedAssays = buildAddedSelectionParamList(uniqueGroups, currentAssays, assay);
              if (!updatedAssays || updatedAssays.length === 0) {
                next.delete('ggroups');
              } else {
                next.set('ggroups', updatedAssays.join(','));
              }
              return next;
            },
            { replace: true },
          );
        }
        return;
      }
      const onlySampleSelectedNo = selectedSamples?.length === 1 && selectedSamples[0] === sample;
      const onlyAssaySelectedNo = selectedGroups?.length === 1 && selectedGroups[0] === assay;
      if (onlySampleSelectedNo && onlyAssaySelectedNo) {
        setSearchParamsStable(
          prev => {
            const next = new URLSearchParams(prev);
            next.delete('samples');
            next.delete('ggroups');
            return next;
          },
          { replace: true },
        );
      } else {
        setSearchParamsStable(
          prev => {
            const next = new URLSearchParams(prev);
            next.set('samples', sample);
            next.set('ggroups', assay);
            return next;
          },
          { replace: true },
        );
      }
      lastSampleRef.current = sample;
      lastAssayRef.current = assay;
    },
    [
      selectedSamples,
      selectedGroups,
      toggleSampleStable,
      toggleGroupStable,
      setSearchParamsStable,
      sampleKeys,
      uniqueGroups,
    ],
  );

  const isSampleVisible = useCallback(
    (sample: string) => (selectedSamples ? selectedSamples.includes(sample) : true),
    [selectedSamples],
  );
  const isGroupVisible = useCallback(
    (assay: string) => (selectedGroups ? selectedGroups.includes(assay) : true),
    [selectedGroups],
  );

  const visibleData = useMemo(
    () => debugData.filter(d => isSampleVisible(d.cts.sample) && isGroupVisible(d.cts.assay)),
    [debugData, isSampleVisible, isGroupVisible],
  );

  const sampleLegendItems = useMemo(() => {
    return sampleKeys.map(key => {
      const sampleName = debugData.find(d => d.cts.sample === key)?.sampleName || '';
      return { label: key, displayLabel: `${key} - ${sampleName}`, color: colorAssignments.sampleColor[key] };
    });
  }, [sampleKeys, debugData, colorAssignments.sampleColor]);

  const rejectionById = useMemo(() => {
    return debugData.reduce<Record<number, number | 'M' | null>>((acc, datum) => {
      if (isQaResultLabelDisqualifying(datum.cts.qaResultLabelType ?? null)) {
        acc[datum.cts.id] = 'M';
      } else if (datum.cts.qaResultLabelType != null) {
        acc[datum.cts.id] = null;
      } else {
        acc[datum.cts.id] = datum.cts.qaRuleId ?? null;
      }
      return acc;
    }, {});
  }, [debugData]);

  const heatmapData = useMemo(
    () =>
      debugData.map(d => ({
        row: d.cts.row,
        column: d.cts.column,
        assay: d.cts.assay,
        sample: d.cts.sample,
        sampleName: d.sampleName,
        sampleType: d.sampleType,
        sampleSubtype: d.sampleSubtype,
        ct: d.cts.ct,
        tm: d.cts.tm,
        efficiency: d.cts.efficiency,
        flags: d.cts.flags,
        selectedAssays: selectedGroups,
        rejectRule: rejectionById[d.cts.id] ?? null,
        qaResultLabelType: d.cts.qaResultLabelType ?? null,
        qaResultLabelScope: d.cts.qaResultLabelScope ?? null,
        qaLabels: d.cts.qaLabels ?? [],
      })),
    [debugData, selectedGroups, rejectionById],
  );

  const { flaggedAssays, flaggedSamples } = useMemo(() => getNoAmplificationHighlights(warnings), [warnings]);

  const setWarningFocusScopeStable = useCallback(
    (scope: { samples: string[] | null; groups: string[] | null }) => {
      setSearchParamsStable(
        prev => {
          const next = new URLSearchParams(prev);
          if (scope.samples == null || scope.samples.length === 0) {
            next.delete('samples');
          } else {
            next.set('samples', scope.samples.join(','));
          }
          if (scope.groups == null || scope.groups.length === 0) {
            next.delete('ggroups');
          } else {
            next.set('ggroups', scope.groups.join(','));
          }
          return next;
        },
        { replace: true },
      );
      lastSampleRef.current = scope.samples?.[0] ?? null;
      lastAssayRef.current = scope.groups?.[0] ?? null;
    },
    [setSearchParamsStable],
  );

  const focusSampleStable = useCallback(
    (technicalSample: string) => {
      setWarningFocusScopeStable(
        getChipWarningSampleFocusSelection(
          {
            selectedSamples,
            selectedGroups,
          },
          [technicalSample],
        ),
      );
    },
    [selectedSamples, selectedGroups, setWarningFocusScopeStable],
  );

  const focusGroupStable = useCallback(
    (assay: string) => {
      setWarningFocusScopeStable(
        getChipWarningAssayFocusSelection(
          {
            selectedSamples,
            selectedGroups,
          },
          assay,
        ),
      );
    },
    [selectedSamples, selectedGroups, setWarningFocusScopeStable],
  );

  const focusBiologicalSampleStable = useCallback(
    (baseSampleId: string) => {
      const reps = sampleKeys.filter(k => getSampleId(k) === baseSampleId);
      if (reps.length === 0) {
        return;
      }
      setWarningFocusScopeStable(
        getChipWarningSampleFocusSelection(
          {
            selectedSamples,
            selectedGroups,
          },
          reps,
        ),
      );
    },
    [sampleKeys, getSampleId, selectedSamples, selectedGroups, setWarningFocusScopeStable],
  );

  const focusBiologicalSampleAssayStable = useCallback(
    (baseSampleId: string, assay: string) => {
      const reps = sampleKeys.filter(k => getSampleId(k) === baseSampleId);
      if (reps.length === 0) {
        return;
      }
      setWarningFocusScopeStable(
        getChipWarningSampleAssayFocusSelection(
          {
            selectedSamples,
            selectedGroups,
          },
          reps,
          assay,
        ),
      );
    },
    [sampleKeys, getSampleId, selectedSamples, selectedGroups, setWarningFocusScopeStable],
  );

  const ampCurves: Curve[] = useMemo(
    () =>
      visibleData
        .map(d => {
          if (!d.amplificationCurve) return null;
          const curveColor =
            colorAssignments.mode === 'ASSAY'
              ? colorAssignments.assayColor[d.cts.assay]
              : colorAssignments.sampleColor[d.cts.sample];
          const curve: Curve = {
            xs: d.amplificationCurve.xs,
            ys: d.amplificationCurve.ys,
            color: curveColor,
            id: `${d.cts.sample}-amp-${d.cts.assay}-${d.cts.id}`,
            markerX: Number.isFinite(d.cts.ct) ? (d.cts.ct as number) : undefined,
            isRejected: rejectionById[d.cts.id] != null,
            isNotDetected: d.cts.ct == null,
          };
          return curve;
        })
        .filter(Boolean) as Curve[],
    [visibleData, colorAssignments.mode, colorAssignments.assayColor, colorAssignments.sampleColor, rejectionById],
  );

  const derCurves: Curve[] = useMemo(
    () =>
      visibleData
        .map(d => {
          if (!d.derivateCurve) return null;
          const curveColor =
            colorAssignments.mode === 'ASSAY'
              ? colorAssignments.assayColor[d.cts.assay]
              : colorAssignments.sampleColor[d.cts.sample];
          const curve: Curve = {
            xs: d.derivateCurve.xs,
            ys: d.derivateCurve.ys,
            color: curveColor,
            id: `${d.cts.sample}-der-${d.cts.assay}-${d.cts.id}`,
            markerX: Number.isFinite(d.cts.tm) ? (d.cts.tm as number) : undefined,
            isRejected: rejectionById[d.cts.id] != null,
            isNotDetected: d.cts.ct == null,
          };
          return curve;
        })
        .filter(Boolean) as Curve[],
    [visibleData, colorAssignments.mode, colorAssignments.assayColor, colorAssignments.sampleColor, rejectionById],
  );

  const [selectedMetric, setSelectedMetricStable] = useState<'ct' | 'efficiency'>('ct');

  const flagOptions = useMemo(() => {
    const opts = availableFlags.map((flag: SmartChipFlag) => ({ label: flag, value: flag as ChipViewHighlightMode }));
    return [...opts, { label: 'Mark selected', value: chipViewHighlightSelectedValue }];
  }, []);
  const [storedHighlightFlag, setStoredHighlightFlagStable] = useLocalStorageState<StoredChipViewHighlightValue>(
    LocalStorageId.chipViewHighlightFlag,
    {
      defaultValue: chipViewHighlightSelectedValue,
    },
  );
  const highlightFlag = useMemo(() => getChipViewHighlightMode(storedHighlightFlag), [storedHighlightFlag]);
  const setHighlightFlagStable = useCallback<React.Dispatch<React.SetStateAction<ChipViewHighlightMode>>>(
    value => {
      setStoredHighlightFlagStable(prevStoredValue => {
        const previousValue = getChipViewHighlightMode(prevStoredValue);
        return typeof value === 'function' ? value(previousValue) : value;
      });
    },
    [setStoredHighlightFlagStable],
  );
  const [showRejected, setShowRejectedStable] = useLocalStorageState<boolean>(
    LocalStorageId.chipViewShowRejected as string,
    {
      defaultValue: false,
    },
  );
  const focusFlagStable = useCallback(
    (flag: SmartChipFlag) => {
      setHighlightFlagStable(previousFlag =>
        previousFlag === flag ? chipViewHighlightSelectedValue : flag,
      );
    },
    [setHighlightFlagStable],
  );

  const contextValue: ChipContextData = {
    loading: isLoading,
    chipInfo,
    workbookLink,
    projects,
    qaLabels,
    warnings,
    selectedSamples,
    selectedGroups,
    sampleKeys,
    uniqueGroups,
    assayColorByAssay: colorAssignments.assayColor,
    sampleColorByKey: colorAssignments.sampleColor,
    colorMode: colorAssignments.mode,
    heatmapData,
    ampCurves,
    derCurves,
    flaggedAssays,
    flaggedSamples,
    sampleLegendItems,
    selectedMetric,
    setSelectedMetricStable,
    highlightFlag,
    setHighlightFlagStable,
    flagOptions,
    showRejected,
    setShowRejectedStable,
    toggleSampleStable,
    toggleGroupStable,
    handleCellClickStable,
    focusSampleStable,
    focusGroupStable,
    focusBiologicalSampleStable,
    focusBiologicalSampleAssayStable,
    focusFlagStable,
    debugData,
    rejectionById,
  };

  return <ChipContext.Provider value={contextValue}>{children}</ChipContext.Provider>;
}

export function useChipContext() {
  const ctx = useContext(ChipContext);
  if (!ctx) {
    throw new Error('useChipContext must be used within a ChipContextProvider');
  }
  return ctx;
}
